import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { MapPin, Search, Filter, SlidersHorizontal, ArrowRight, X } from "lucide-react";
import { PROPERTIES, FORMS } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

const LOGO = "/sneha-logo.jpg";

const Properties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ tag: "", status: "", developer: "" });
  const [showFilters, setShowFilters] = useState(false);

  const tags = ["All", "Luxury", "Investor Picks", "Pre-launch", "Villas", "Commercial", "Ready to Move"];
  const statuses = ["All", "Ready to Move", "Under Construction", "New Launch", "Pre-Launch"];
  const developers = ["All", "Godrej Properties", "Hiranandani Group", "Adani Realty", "L&T Realty", "Prestige Group", "Shapoorji Pallonji"];

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const params = {};
        if (filters.tag && filters.tag !== "All") params.tag = filters.tag;
        if (filters.status && filters.status !== "All") params.status = filters.status;
        if (filters.developer && filters.developer !== "All") params.developer = filters.developer;
        const res = await axios.get(`${API}/properties`, { params });
        setProperties(res.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    fetchProperties();
  }, [filters]);

  const filtered = search
    ? properties.filter((p) =>
        p.title?.toLowerCase().includes(search.toLowerCase()) ||
        p.location?.toLowerCase().includes(search.toLowerCase()) ||
        p.developer?.toLowerCase().includes(search.toLowerCase())
      )
    : properties;

  return (
    <div className="bg-[#080808] min-h-screen pt-24">
      {/* Header */}
      <div className="py-16 border-b border-white/5"
        style={{ background: "linear-gradient(180deg, #0a0900 0%, #080808 100%)" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p {...fadeUp} className="section-label">All Properties</motion.p>
          <motion.h1 {...fadeUp} transition={{ delay: 0.1 }}
            className="font-display text-4xl md:text-5xl font-light text-[#FAF8F3] mb-4">
            Curated Investment<br />
            <span className="text-gold-gradient italic">Opportunities</span>
          </motion.h1>
          <motion.p {...fadeUp} transition={{ delay: 0.2 }} className="text-[#666] text-sm max-w-xl">
            Hand-picked projects in Navi Mumbai's highest appreciation zones — backed by infrastructure intelligence.
          </motion.p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Search + Filter bar */}
        <motion.div {...fadeUp} className="flex flex-col md:flex-row gap-4 mb-8" data-testid={PROPERTIES.filterBar}>
          <div className="flex-1 relative">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#666]" />
            <input
              data-testid={PROPERTIES.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search properties, locations, developers..."
              className="w-full bg-[#0a0a0a] border border-white/10 pl-10 pr-4 py-3 text-sm text-[#FAF8F3] outline-none focus:border-[#D4AF37]/40 transition-colors placeholder:text-[#444]"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 border px-5 py-3 text-xs tracking-wider uppercase transition-all ${
              showFilters ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-[#666]"
            }`}
          >
            <SlidersHorizontal size={14} /> Filters
          </button>
        </motion.div>

        {/* Filters panel */}
        {showFilters && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="glass-gold p-6 mb-8 grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {/* Tag filter */}
            <div>
              <p className="text-xs text-[#D4AF37] tracking-wider uppercase mb-3">Category</p>
              <div className="flex flex-wrap gap-2">
                {tags.map((t) => (
                  <button key={t} onClick={() => setFilters({ ...filters, tag: t === "All" ? "" : t })}
                    className={`text-[10px] px-3 py-1.5 border tracking-wider transition-all ${
                      (filters.tag || "All") === t ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-[#666]"
                    }`}
                  >{t}</button>
                ))}
              </div>
            </div>

            {/* Status filter */}
            <div>
              <p className="text-xs text-[#D4AF37] tracking-wider uppercase mb-3">Status</p>
              <div className="flex flex-wrap gap-2">
                {statuses.map((s) => (
                  <button key={s} onClick={() => setFilters({ ...filters, status: s === "All" ? "" : s })}
                    className={`text-[10px] px-3 py-1.5 border tracking-wider transition-all ${
                      (filters.status || "All") === s ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-[#666]"
                    }`}
                  >{s}</button>
                ))}
              </div>
            </div>

            {/* Developer filter */}
            <div>
              <p className="text-xs text-[#D4AF37] tracking-wider uppercase mb-3">Developer</p>
              <select value={filters.developer || "All"} onChange={(e) => setFilters({ ...filters, developer: e.target.value === "All" ? "" : e.target.value })}
                className="w-full bg-transparent border border-white/10 px-3 py-2 text-xs text-[#FAF8F3] outline-none focus:border-[#D4AF37]/40"
              >
                {developers.map((d) => <option key={d} value={d} className="bg-[#111]">{d}</option>)}
              </select>
            </div>
          </motion.div>
        )}

        {/* Results count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-xs text-[#666] tracking-wider">
            Showing <span className="text-[#D4AF37]">{filtered.length}</span> properties
          </p>
          {(filters.tag || filters.status || filters.developer || search) && (
            <button onClick={() => { setFilters({ tag: "", status: "", developer: "" }); setSearch(""); }}
              className="flex items-center gap-2 text-xs text-[#666] hover:text-[#FAF8F3] transition-colors"
            >
              <X size={12} /> Clear all
            </button>
          )}
        </div>

        {/* Property grid */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="w-8 h-8 border border-[#D4AF37]/20 border-t-[#D4AF37] rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={PROPERTIES.grid}>
            {filtered.map((p, i) => (
              <motion.div key={p.id}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.7 }}
              >
                <Link to={`/properties/${p.id}`} data-testid={PROPERTIES.card} className="block gold-card bg-[#0a0a0a] group">
                  {/* Image */}
                  <div className="relative h-52 overflow-hidden">
                    <img src={p.image} alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                    <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
                      {(p.tags || []).slice(0, 2).map((tag) => (
                        <span key={tag} className="text-[9px] tracking-wider uppercase px-2 py-1 bg-black/70 text-[#D4AF37] border border-[#D4AF37]/20">{tag}</span>
                      ))}
                    </div>
                    <div className="absolute bottom-3 right-3">
                      <p className="font-display text-lg text-[#D4AF37]">{p.price_display}</p>
                    </div>
                  </div>

                  <div className="p-5">
                    <h3 className="font-heading text-[#FAF8F3] font-light text-base mb-1 group-hover:text-[#D4AF37] transition-colors duration-300">
                      {p.title}
                    </h3>
                    <div className="flex items-center gap-1 text-xs text-[#666] mb-2">
                      <MapPin size={10} className="text-[#D4AF37]/60 shrink-0" />
                      <span>{p.location}</span>
                    </div>
                    <p className="text-[10px] text-[#555] mb-4">{p.developer}</p>

                    {/* Configs */}
                    <div className="flex flex-wrap gap-1 mb-4">
                      {(p.configurations || []).slice(0, 3).map((c) => (
                        <span key={c} className="text-[9px] border border-white/10 px-2 py-1 text-[#666]">{c}</span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-white/5">
                      <div className="flex gap-4">
                        <div className="text-center">
                          <p className="text-[#D4AF37] text-sm font-display">{p.roi_score}</p>
                          <p className="text-[#444] text-[9px] tracking-wider">ROI</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[#D4AF37] text-sm font-display">{p.growth_score}</p>
                          <p className="text-[#444] text-[9px] tracking-wider">Growth</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[#D4AF37] text-sm font-display">{p.annual_rental_yield}</p>
                          <p className="text-[#444] text-[9px] tracking-wider">Yield</p>
                        </div>
                      </div>
                      <span className={`text-[9px] border px-2 py-1 tracking-wider ${
                        p.status === "Ready to Move" ? "border-green-800/40 text-green-600" :
                        p.status === "New Launch" ? "border-[#D4AF37]/40 text-[#D4AF37]" :
                        "border-white/10 text-[#555]"
                      }`}>{p.status}</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {filtered.length === 0 && !loading && (
          <div className="text-center py-24">
            <p className="text-[#666] text-sm mb-4">No properties found matching your criteria.</p>
            <button onClick={() => { setFilters({ tag: "", status: "", developer: "" }); setSearch(""); }}
              className="btn-gold text-xs py-3 px-6">Clear Filters</button>
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 text-center glass-gold p-10">
          <img src={LOGO} alt="Sneha Reality" className="h-16 w-auto object-contain mx-auto mb-4" />
          <h3 className="font-display text-2xl text-[#FAF8F3] mb-3">Can't Find the Right Property?</h3>
          <p className="text-[#666] text-sm mb-6">Our advisors have access to exclusive pre-launch and off-market opportunities not listed here.</p>
          <Link to="/consultation" className="btn-gold-solid btn-gold text-xs py-3 px-8">
            Get Private Advisory <ArrowRight size={12} className="inline ml-2" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Properties;
